/**
 * @file ui/src/pages/Support.tsx
 * @description Public bilingual support page for Spark KNE.
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { useTranslation } from 'react-i18next'
import { setLocale as persistLocale } from '../i18n'
import styles from './Home.module.css'

type Locale = 'en' | 'zh'

const copy = {
  en: {
    navLabel: 'Support navigation',
    switchLabel: '中文',
    title: 'Spark KNE Support',
    updated: 'Last updated: August 10, 2026',
    intro: 'Get help with installation, consent, sign-in, selected-text verification, sources, quotas, contributions, or account and data requests.',
    emailLabel: 'Email support',
    sections: [
      ['Before contacting support', [
        'Update Spark KNE Verify to the latest published version and restart the browser.',
        'Confirm that you explicitly selected text before choosing Verify. Spark does not continuously scan pages or monitor the clipboard.',
        'If a result shows source_missing, it means KNE found no applicable source within its current coverage; it is not necessarily a connection error.',
      ]],
      ['What to include', [
        'Include the extension or integration version, browser or platform, steps taken, expected result, actual result, and any visible request ID or error category.',
        'Do not send passwords, access tokens, API keys, payment information, government identifiers, private medical records, or other unnecessary sensitive data.',
        'Only include the selected claim text when it is necessary to reproduce the verification issue and you have the right to share it.',
      ]],
      ['Account, privacy, and deletion', [
        'For account access, Verify history, contribution status, quota, privacy, or deletion requests, email seth@knespark.com from the account email when possible.',
        'The Privacy Policy explains local and server storage, retention, sharing, security, and deletion options.',
      ]],
      ['Supported release channels', [
        'Public support applies only to Spark KNE versions and integrations that are listed on knespark.com or an official platform store under Spark KNE.',
        'Unpublished local builds, disabled adapters, and experimental MCP or Coze integrations are not public product availability claims.',
      ]],
    ],
  },
  zh: {
    navLabel: '支持页面导航',
    switchLabel: 'EN',
    title: 'Spark KNE 支持',
    updated: '最后更新：2026 年 8 月 10 日',
    intro: '获取安装、数据同意、登录、选中文本验证、来源、额度、知识贡献、账号或数据请求方面的帮助。',
    emailLabel: '发送支持邮件',
    sections: [
      ['联系支持前', [
        '将 Spark KNE Verify 更新到平台已发布的最新版本，并重新启动浏览器。',
        '确认先主动选中文本，再选择 Verify。Spark 不会持续扫描页面，也不会监听剪贴板。',
        '如果结果显示 source_missing，表示 KNE 当前覆盖内没有找到适用来源，并不一定是连接错误。',
      ]],
      ['需要提供的信息', [
        '请提供插件或集成版本、浏览器或平台、操作步骤、预期结果、实际结果，以及页面显示的 request ID 或错误类别。',
        '请勿发送密码、访问 token、API key、支付信息、政府身份号码、私人病历或其它非必要敏感数据。',
        '只有在复现验证问题确有必要且你有权分享时，才提供对应的选中主张文本。',
      ]],
      ['账号、隐私与删除', [
        '账号访问、Verify 历史、贡献状态、额度、隐私或删除请求，请尽可能使用账号对应邮箱发送至 seth@knespark.com。',
        '隐私政策说明本地和服务端存储、保留、共享、安全及删除方式。',
      ]],
      ['支持的发布渠道', [
        '公开支持仅适用于 knespark.com 或官方平台商店中以 Spark KNE 名义列出的版本和集成。',
        '尚未发布的本地构建、关闭的适配器，以及实验中的 MCP 或 Coze 集成不代表公开产品已可用。',
      ]],
    ],
  },
} as const

export default function SupportPage() {
  const { i18n } = useTranslation()
  const locale: Locale = i18n.language === 'en' ? 'en' : 'zh'
  const t = copy[locale]

  return (
    <main className={styles.root}>
      <section className={styles.policyPage}>
        <nav className={styles.nav} aria-label={t.navLabel}>
          <a className={styles.brand} href="/">
            <span className={styles.brandMark} />
            <span>Spark KNE</span>
          </a>
          <button
            className={styles.langToggle}
            type="button"
            onClick={() => persistLocale(locale === 'en' ? 'zh' : 'en')}
          >
            {t.switchLabel}
          </button>
        </nav>
        <header className={styles.policyHeader}>
          <p className={styles.kicker}>Support</p>
          <h1>{t.title}</h1>
          <span>{t.updated}</span>
          <p>{t.intro}</p>
          <p><a href="mailto:seth@knespark.com">{t.emailLabel}: seth@knespark.com</a></p>
        </header>
        <div className={styles.policySections}>
          {t.sections.map(([title, items]) => (
            <section key={title} className={styles.policyBlock}>
              <h2>{title}</h2>
              <ul>
                {items.map(item => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </main>
  )
}
